import React from "react";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import { blue } from "@mui/material/colors";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment from "moment-timezone";
const MAX_IN_MINUTES = 90*1440
const WindowLabel = (props) => {
  const { showWarning = true } = props;
  return (
    <FormLabel
      component="legend"
      style={{
        fontSize: 14,
        fontStyle: "italic",
        marginTop: 12,
      }}
    >
      {!showWarning ? (
        "Window"
      ) : (
        <>
          Window (maximum)
          <Tooltip title="Based upon the event rates, you may not be able to use the full window requested.">
            <InfoIcon fontSize="small" style={{ color: blue[500] }} />
          </Tooltip>
        </>
      )}
    </FormLabel>
  );
};

const TimeDurationField = (props) => {
  const { timeframe, fieldId, errors, setOptions, validations, validate } =
    props;
  return (
    <TextField
      variant="standard"
      error={errors["timeDurationError"]}
      id={fieldId}
      label="Length"
      value={timeframe["length.value"]}
      onChange={(event) => {
        setOptions({
          timeframe: {
            ...timeframe,
            "length.value": event.target.value,
          },
        });
        if (validate) {
          validations(event, timeframe, "number");
        }
      }}
      type="number"
      InputLabelProps={{ shrink: true }}
      margin="normal"
      style={{ width: "50%" }}
    />
  );
};

const TimeUnitsField = (props) => {
  const { timeframe, fieldId, setOptions, validations, validate } = props;
  return (
    <TextField
      id={fieldId}
      select
      label="Units"
      value={timeframe["length.units"]}
      onChange={(event) => {
        setOptions({
          timeframe: {
            ...timeframe,
            "length.units": event.target.value,
          },
        });
        if (validate) {
          validations(event, timeframe, "units");
        }
      }}
      margin="normal"
      variant="filled"
      style={{ marginLeft: 6, marginTop: 8, width: "50%" }}
      SelectProps={{
        sx: { backgroundColor: "transparent" },
      }}
    >
      <MenuItem key="minutes" value="minutes">
        Minutes
      </MenuItem>
      <MenuItem key="hours" value="hours">
        Hours
      </MenuItem>
      <MenuItem key="days" value="days">
        Days
      </MenuItem>
    </TextField>
  );
};

const TimeframeOption = (props) => {
  const convertToMinutes = (units) => {
    if (units === "minutes") {
      return 1;
    } else if (units === "hours") {
      return 60;
    } else if (units === "days") {
      return 60 * 24;
    }
  };

  const errorMessages = {
    timeDurationError: {
      minutes: "Please enter a value between 1 and 129,600 minute(s)",
      hours: "Please enter a value between 1 and 2160 hour(s)",
      days: "Please enter a value between 1 and 90 day(s)"
    },
  };

  const errorMessage = (units) => {
    let errorFound = false;
    for (let error in props.errors) {
      if (error.includes("time") && !errorFound) {
        errorFound = true;
        return (
          <p style={{ color: "red", fontSize: "10px" }}>
            {errorMessages[error][units]}
          </p>
        );
      }
    }
  };

  const validations = (event, timeframe, type) => {
    let id;
    let units;
    let value;

    if (type === "units") {
      id = "timeDurationError";
      units = event.target.value;
      value = timeframe["length.value"];
    } else if (type === "number") {
      id = "timeDurationError";
      units = timeframe["length.units"];
      value = event.target.value;
    }
    let valueAdjustedtoMinutes = convertToMinutes(units) * value;
    if (valueAdjustedtoMinutes <= (MAX_IN_MINUTES) && valueAdjustedtoMinutes >= 0) {
      props.noErrorDetected(id);
    } else {
      props.errorDetected(id);
    }
  };

  const timeframe = props.options.timeframe;
  const timezone = props.options.timezone;

  return (
    <FormControl
      variant="standard"
      style={{ width: "calc(100% - 48px)", margin: 24 }}
    >
      <FormLabel component="legend">Time Frame</FormLabel>
      <RadioGroup
        aria-label="Timeframe Type"
        name="timeframe-type"
        value={timeframe.type}
        row={true}
        onChange={(event) => {
          const value = event.target.value;
          let newOptions = { ...timeframe, type: value };
          if (value === "fixed" && timeframe.start == null) {
            newOptions.start = moment();
          }
          props.setOptions({ timeframe: newOptions });
        }}
      >
        <FormControlLabel value="moving" control={<Radio />} label="Moving" />
        <FormControlLabel value="fixed" control={<Radio />} label="Fixed" />
      </RadioGroup>
      {timeframe.type === "fixed" && (
        <div style={{ float: "right" }}>
          <Grid container direction="column">
            <Grid item>
              <DateTimePicker
                key={timezone || "local"}
                error={false}
                id="starttime"
                label="Start"
                timeSteps={{hours: 1, minutes: 1, seconds: 1}}
                value={
                  timezone
                    ? moment.tz(timeframe.start, timezone)
                    : moment(timeframe.start)
                }
                onChange={(dt) => {
                  props.setOptions({
                    timeframe: { ...timeframe, start: dt.valueOf() },
                  });
                }}
                slotProps={{ textField: { variant: "standard" } }}
                style={{ width: "100%", maxWidth: "100%", margin: 6 }}
              />
            </Grid>
            <Grid item>
              <WindowLabel showWarning={props.showWarning} />
              <FormControl
                variant="standard"
                style={{ flexDirection: "row", margin: 6, width: "100%" }}
              >
                <TimeDurationField
                  timeframe={timeframe}
                  fieldId={"select-fixed-length"}
                  errors={props.errors}
                  setOptions={props.setOptions}
                  validate={true}
                  validations={validations}
                />
                <TimeUnitsField
                  timeframe={timeframe}
                  fieldId={"select-fixed-units"}
                  setOptions={props.setOptions}
                  validate={true}
                  validations={validations}
                />
              </FormControl>

              {errorMessage(timeframe["length.units"])}
            </Grid>
          </Grid>
        </div>
      )}
      {timeframe.type === "moving" && (
        <FormControl variant="standard">
          <FormControl variant="standard">
            <WindowLabel showWarning={props.showWarning} />
            <FormControl
              variant="standard"
              fullWidth={true}
              style={{ flexDirection: "row" }}
            >
              <TimeDurationField
                timeframe={timeframe}
                fieldId={"select-moving-length"}
                errors={props.errors}
                setOptions={props.setOptions}
                validate={true}
                validations={validations}
              />
              <TimeUnitsField
                timeframe={timeframe}
                fieldId={"select-moving-units"}
                setOptions={props.setOptions}
                validate={true}
                validations={validations}
              />
              {errorMessage(timeframe["length.units"])}
            </FormControl>
          </FormControl>
        </FormControl>
      )}
      <FormControl variant="standard">
        <FormControl
          variant="standard"
          fullWidth={true}
          style={{ flexDirection: "row", alignItems: "center" }}
        >
          <FormControl
            variant="standard"
            component="fieldset"
            sx={{ marginLeft: 0, marginRight: 0, width: "100%" }}
          >
            <FormLabel
              component="legend"
              style={{ fontSize: 14, fontStyle: "italic" }}
            >
              Refresh Interval
            </FormLabel>
            <RadioGroup
              aria-label="Interval Type"
              name="interval-type"
              value={timeframe["interval.type"] || "set"}
              row={true}
              onChange={(event) => {
                const value = event.target.value;
                let newOptions = {
                  ...timeframe,
                  "interval.type": value,
                };
                props.setOptions({ timeframe: newOptions });
              }}
            >
              <FormControlLabel value="auto" control={<Radio />} label="Auto" />
              <FormControlLabel value="set" control={<Radio />} label="Set" />
            </RadioGroup>
            <FormControl
              variant="standard"
              fullWidth={true}
              style={{ flexDirection: "row" }}
            >
              {timeframe["interval.type"] !== "auto" &&
                timeframe["interval.units"] === "seconds" && (
                  <TextField
                    id="select-interval-seconds"
                    select
                    label="Length"
                    value={timeframe["interval.value"]}
                    style={{ marginTop: 0, width: "50%" }}
                    onChange={(event) =>
                      props.setOptions({
                        timeframe: {
                          ...timeframe,
                          "interval.value": event.target.value,
                        },
                      })
                    }
                    margin="normal"
                    variant="filled"
                    SelectProps={{
                      sx: { backgroundColor: "transparent" },
                    }}
                  >
                    <MenuItem key="s5" value="5">
                      5
                    </MenuItem>
                    <MenuItem key="s10" value="10">
                      10
                    </MenuItem>
                    <MenuItem key="s20" value="20">
                      20
                    </MenuItem>
                    <MenuItem key="s30" value="30">
                      30
                    </MenuItem>
                    <MenuItem key="s40" value="40">
                      40
                    </MenuItem>
                    <MenuItem key="s50" value="50">
                      50
                    </MenuItem>
                  </TextField>
                )}
              {timeframe["interval.type"] !== "auto" &&
                timeframe["interval.units"] !== "seconds" && (
                  <TextField
                    variant="standard"
                    id="select-interval-nonSeconds"
                    label="Refresh interval"
                    value={
                      timeframe["interval.value"] //changed id so error works, make sure nothing else breaks
                    }
                    onChange={(event) => {
                      props.setOptions({
                        timeframe: {
                          ...timeframe,
                          "interval.value": event.target.value,
                        },
                      });
                    }}
                    type="number"
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    style={{ width: "50%", marginTop: 8 }}
                  />
                )}
              {timeframe["interval.type"] !== "auto" && (
                <TextField
                  id="select-update-time-units"
                  select
                  label="Units"
                  value={timeframe["interval.units"]}
                  onChange={(event) => {
                    const val = event.target.value;
                    let update = {
                      ...timeframe,
                      "interval.units": val,
                    };
                    if (
                      val === "seconds" &&
                      timeframe["interval.value"] % 10 > 0
                    ) {
                      update["interval.value"] = "10";
                    }
                    return props.setOptions({ timeframe: update });
                  }}
                  margin="normal"
                  variant="filled"
                  style={{ marginLeft: 6, marginTop: 0, width: "50%" }}
                  SelectProps={{
                    sx: { backgroundColor: "transparent" },
                  }}
                >
                  <MenuItem key="seconds" value="seconds">
                    Seconds
                  </MenuItem>
                  <MenuItem key="minutes" value="minutes">
                    Minutes
                  </MenuItem>
                  <MenuItem key="hours" value="hours">
                    Hours
                  </MenuItem>
                </TextField>
              )}
            </FormControl>
          </FormControl>
        </FormControl>
      </FormControl>
    </FormControl>
  );
};

export default TimeframeOption;
